import { normalizePhone } from 'cpool-schemas';
import type { SchemaChatResolver_GetChats } from 'cpool-tables/backend-resolvers';
import type { ValueOf } from 'cpool-utils';

export const APPLICANT_FILTERS = {
    BOTH: 'BOTH',
    KN1: 'KN1',
    KN2: 'KN2',
} as const;

export type ApplicantFilter = ValueOf<typeof APPLICANT_FILTERS>;

type Chat = SchemaChatResolver_GetChats[number];

type LeadApplicant = {
    personendaten: {
        anrede?: string | null;
        nachname: string;
        telefonPrivat?: string | null;
        vorname: string;
    };
};

export type LeadWithApplicants = {
    antragsteller1: LeadApplicant;
    antragsteller2?: LeadApplicant | null;
};

export type ApplicantChatSlot = {
    chatId: string | null;
    chatIds: Array<string>;
    displayName: string;
    isForeignNumber: boolean;
    normalizedPhone: string | null;
};

export type ApplicantChatResolution = {
    hasCoApplicant: boolean;
    kn1: ApplicantChatSlot;
    kn2: ApplicantChatSlot | null;
};

const formatApplicantName = (applicant: LeadApplicant): string =>
    `${applicant.personendaten.vorname} ${applicant.personendaten.nachname}`.trim();

const toNormalizedPhone = (phone: string | null | undefined): string | null => (phone ? normalizePhone(phone) : null);

const resolveSlot = (
    applicant: LeadApplicant,
    chats: Array<Chat>,
    ownPhoneNumberId: string | null | undefined,
): ApplicantChatSlot => {
    const normalizedPhone = toNormalizedPhone(applicant.personendaten.telefonPrivat);
    const matches = normalizedPhone
        ? chats
              .filter((chat) => chat.phone && toNormalizedPhone(chat.phone) === normalizedPhone)
              .sort((left, right) => (right.lastMessageAt ?? 0) - (left.lastMessageAt ?? 0))
        : [];
    const ownNumberChat = ownPhoneNumberId
        ? matches.find((chat) => chat.phoneNumberId === ownPhoneNumberId)
        : undefined;

    return {
        chatId: (ownNumberChat ?? matches[0])?.id ?? null,
        chatIds: matches.map((chat) => chat.id),
        displayName: formatApplicantName(applicant),
        isForeignNumber: Boolean(ownPhoneNumberId) && matches.length > 0 && !ownNumberChat,
        normalizedPhone,
    };
};

export const resolveApplicantChats = (
    lead: LeadWithApplicants,
    chats: Array<Chat>,
    ownPhoneNumberId: string | null | undefined,
): ApplicantChatResolution => {
    const kn1 = resolveSlot(lead.antragsteller1, chats, ownPhoneNumberId);

    if (!lead.antragsteller2) {
        return { hasCoApplicant: false, kn1, kn2: null };
    }

    return { hasCoApplicant: true, kn1, kn2: resolveSlot(lead.antragsteller2, chats, ownPhoneNumberId) };
};

export const inferApplicantFilter = (chatId: string, resolution: ApplicantChatResolution): ApplicantFilter => {
    if (resolution.kn2?.chatIds.includes(chatId)) return APPLICANT_FILTERS.KN2;
    return APPLICANT_FILTERS.KN1;
};

export const resolveChatIdForFilter = (filter: ApplicantFilter, resolution: ApplicantChatResolution): string | null => {
    if (filter === APPLICANT_FILTERS.KN1) return resolution.kn1.chatId;
    if (filter === APPLICANT_FILTERS.KN2) return resolution.kn2?.chatId ?? null;
    return resolution.kn1.chatId ?? resolution.kn2?.chatId ?? null;
};
